import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject
} from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatSort, Sort } from '@angular/material/sort'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { FormBuilder, FormGroup, FormControl } from '@angular/forms'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { MatDialog } from '@angular/material/dialog'
import { CommentDialogComponent } from '../comment-dialog/comment-dialog.component'

export interface ColumnConfig<T> {
  columnDef: string
  type: string
  header: string
  cell: (element: T) => any
  sortable?: boolean
  filterable?: boolean
  isHtml?: boolean
  onClick?: (element: T) => void
  width?: number
  listOptions?: Array<{ value: any; label: string }>
  actions?: { label: string; action: string; icon: string; condition?: (element: T) => boolean }[]
  align?: string
  verticalAlign?: string
}

export interface TableData<T> {
  totalElements: number
  pageSize: number
  pageNumber: number
  content: T[]
  isRowClickable?: boolean
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent<T> implements OnInit, AfterViewInit, OnChanges {
  @Input() heading = ''
  @Input() columns: ColumnConfig<T>[] = []
  @Input() tableData: TableData<T> = {
    totalElements: 0,
    pageSize: 0,
    pageNumber: 0,
    content: [],
    isRowClickable: false
  }

  @Output() pageChange = new EventEmitter<PageEvent>()
  @Output() sortChange = new EventEmitter<{ active: string; direction: string }>()
  @Output() filterChange = new EventEmitter<{ [key: string]: string }>()
  @Output() cellClick = new EventEmitter<{ column: string; element: T; action: string; context?: string }>()
  @Output() rowSelect = new EventEmitter<T>()

  @ViewChild(MatPaginator) paginator: MatPaginator
  @ViewChild(MatSort) sort: MatSort

  displayedColumns: string[] = []
  dataSource = new MatTableDataSource<T>([])
  filterForm: FormGroup
  actionsColumn: { label: string; action: string; condition?: (element: T) => boolean }[]
  @Input() selectedRow: T | null = null
  isRowClickable: boolean = false
  readonly dialog = inject(MatDialog)
  currentRow: T
  currentAction: string

  constructor(private sanitizer: DomSanitizer, private fb: FormBuilder) {
    this.filterForm = this.fb.group({})
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.tableData?.content) {
      this.dataSource.data = this.tableData?.content
    }
    if (this.tableData?.isRowClickable) {
      this.isRowClickable = this.tableData?.isRowClickable
    }

    this.updateActionsColumn()
    this.updateDisplayedColumns()
  }
  updateDisplayedColumns() {
    this.displayedColumns = [...this.columns.map(c => c.columnDef)]
  }

  ngOnInit() {
    this.displayedColumns = this.columns.map(c => c.columnDef)
    this.updateDisplayedColumns()

    this.dataSource.data = this.tableData?.content

    this.columns.forEach(column => {
      if (column.filterable) {
        if (column.type === 'DATE') {
          this.filterForm.addControl(column.columnDef, new FormControl(null))
        } else {
          this.filterForm.addControl(column.columnDef, new FormControl(''))
        }
      }
    })

    this.filterForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(filters => {
      this.applyFilters(filters)
    })
    this.updateActionsColumn()
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort
  }

  applyFilters(filters: { [key: string]: any }) {
    this.filterChange.emit(filters)
  }

  applyFilter(event: Event, column: string) {
    const filterValue = (event.target as HTMLInputElement).value
    this.filterForm.get(column).setValue(filterValue)
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event)
  }

  onSortChange(event: Sort) {
    this.sortChange.emit({ active: event.active, direction: event.direction.toUpperCase() })
  }

  handleCellClick($event) {
    this.cellClick.emit($event)
  }

  handleCellClickForViewDetail(columnName: string, rowData: T) {
    if (columnName == 'expenseDetails') {
      this.selectedRow = rowData
      this.rowSelect.emit(rowData)
    }
  }

  stopPropagation(event: Event) {
    event.stopPropagation()
    event.preventDefault()
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html)
  }

  clearDatepicker(column: string) {
    this.filterForm.get(column).setValue(null)
  }

  updateActionsColumn() {
    const actionColumns = this.columns.filter(column => column.actions)
    this.actionsColumn = actionColumns.length > 0 ? actionColumns[0].actions : []
  }

  handleActionClick(action: string, element: T) {
    this.currentRow = element
    this.currentAction = action
    if (action != 'showPaymentHistoryAction') {
      this.openCommentDialog()
    } else {
      this.cellClick.emit({ column: 'actions', element, action })
    }
  }

  getActionsForElement(element: T) {
    const actionsColumn = this.columns.find(column => column.columnDef === 'actions')
    if (actionsColumn && actionsColumn.actions) {
      return actionsColumn.actions.filter(action => !action.condition || action.condition(element))
    }
    return []
  }

  onRowClick(row: T) {
    this.selectedRow = row
    this.rowSelect.emit(row)
  }

  openCommentDialog(): void {
    const dialogRef = this.dialog.open(CommentDialogComponent, { width: '400px', data: this.currentRow })
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.currentRow = { ...this.currentRow, newComment: result }
        this.cellClick.emit({ column: 'actions', element: this.currentRow, action: this.currentAction })
      }
    })
  }
}
